import { cn } from "../../../lib/utils"
import { Icon } from "../icon/icon"
import type { IconName } from "../icon/icon-registry"

const SIZE = 36
const CENTER = 18
const RADIUS = 14
const STROKE = 2.5
const CIRCUMFERENCE = 2 * Math.PI * RADIUS

export interface JobProgressIndicatorProps {
  /** `null` = measurable total unknown or not started (rotating arc). */
  progressPercent: number | null
  isCancelling?: boolean
  icon?: IconName
}

/**
 * Circular progress ring with a center icon — for background / bulk jobs.
 */
export function JobProgressIndicator({
  progressPercent,
  isCancelling = false,
  icon = "file-text",
}: JobProgressIndicatorProps) {
  if (isCancelling) {
    return (
      <div
        className="flex size-9 shrink-0 items-center justify-center rounded-full border border-border/70 bg-muted/60"
        aria-hidden
      >
        <Icon name="loader" size="xs" className="animate-spin text-muted-foreground" />
      </div>
    )
  }

  const determinate = progressPercent != null
  const pct = determinate ? Math.min(100, Math.max(0, progressPercent)) : 0
  const dashOffset = CIRCUMFERENCE - (pct / 100) * CIRCUMFERENCE

  return (
    <div className="relative flex size-9 shrink-0 items-center justify-center" aria-hidden>
      <svg
        className={cn(
          "absolute inset-0 size-9",
          !determinate && "motion-safe:animate-spin motion-reduce:opacity-80",
        )}
        viewBox={`0 0 ${SIZE} ${SIZE}`}
        fill="none"
      >
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          className="stroke-primary/12"
          strokeWidth={STROKE}
        />
        <circle
          cx={CENTER}
          cy={CENTER}
          r={RADIUS}
          className={cn(
            "stroke-primary",
            determinate && "transition-[stroke-dashoffset] duration-500 ease-out",
          )}
          strokeWidth={STROKE}
          strokeLinecap="round"
          strokeDasharray={
            determinate ? `${CIRCUMFERENCE}` : `${CIRCUMFERENCE * 0.24} ${CIRCUMFERENCE * 0.76}`
          }
          strokeDashoffset={determinate ? dashOffset : undefined}
          style={{
            transform: "rotate(-90deg)",
            transformOrigin: `${CENTER}px ${CENTER}px`,
          }}
        />
      </svg>
      <Icon name={icon} size="xs" className="relative z-[1] text-primary" />
    </div>
  )
}
